import React from 'react';

/**
 * SkeletonBlock — single animated shimmer rectangle.
 */
export function SkeletonBlock({ width = '100%', height = 16, className = '' }) {
  return (
    <span
      className={`adv-skeleton ${className}`}
      style={{ width, height, display: 'block' }}
      aria-hidden="true"
    />
  );
}

/**
 * SkeletonTableRow — one row of the applications table skeleton.
 */
function SkeletonTableRow() {
  return (
    <div className="adv-skeleton-table-row">
      {/* Name */}
      <div style={{ flex: 2, minWidth: 0 }}>
        <SkeletonBlock height={14} width="70%" />
      </div>
      {/* Contact */}
      <div style={{ flex: 2, minWidth: 0 }}>
        <SkeletonBlock height={12} width="80%" />
        <SkeletonBlock height={10} width="55%" className="mt-1" style={{ marginTop: 5 }} />
      </div>
      {/* Status */}
      <div style={{ width: 100, flexShrink: 0 }}>
        <SkeletonBlock height={24} width="100%" style={{ borderRadius: 20 }} />
      </div>
      {/* Status date */}
      <div style={{ width: 130, flexShrink: 0 }}>
        <SkeletonBlock height={12} width="80%" />
      </div>
      {/* Advisor */}
      <div style={{ width: 80, flexShrink: 0 }}>
        <SkeletonBlock height={12} width="60%" />
      </div>
      {/* Docs */}
      <div style={{ width: 60, flexShrink: 0 }}>
        <SkeletonBlock height={12} width="50%" />
      </div>
      {/* Action */}
      <div style={{ width: 72, flexShrink: 0 }}>
        <SkeletonBlock height={32} width="100%" style={{ borderRadius: 8 }} />
      </div>
    </div>
  );
}

/**
 * SkeletonApplicationsTable — animated placeholder for the cases table.
 */
export function SkeletonApplicationsTable({ rows = 8 }) {
  return (
    <>
      {/* Desktop table skeleton */}
      <div className="adv-skeleton-table hidden sm:block" role="status" aria-busy="true" aria-label="Loading cases">
        {/* Header row */}
        <div className="adv-skeleton-table-row" style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          {['flex:2', 'flex:2', '100px', '130px', '80px', '60px', '72px'].map((w, i) => (
            <div key={i} style={{ flex: w.startsWith('flex:') ? Number(w.replace('flex:', '')) : undefined, width: w.startsWith('flex:') ? undefined : w, flexShrink: 0, minWidth: 0 }}>
              <SkeletonBlock height={10} width="50%" style={{ opacity: 0.5 }} />
            </div>
          ))}
        </div>
        {Array.from({ length: rows }).map((_, i) => (
          <SkeletonTableRow key={i} />
        ))}
      </div>

      {/* Mobile card skeletons */}
      <div className="space-y-3 sm:hidden" role="status" aria-busy="true" aria-label="Loading cases">
        {Array.from({ length: Math.min(rows, 5) }).map((_, i) => (
          <div key={i} className="adv-skeleton-card">
            <SkeletonBlock height={14} width="60%" />
            <div style={{ marginTop: 8 }}>
              <SkeletonBlock height={11} width="40%" />
            </div>
            <div style={{ marginTop: 6, display: 'flex', gap: 8 }}>
              <SkeletonBlock height={22} width={80} style={{ borderRadius: 20 }} />
              <SkeletonBlock height={22} width={70} style={{ borderRadius: 20 }} />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

/**
 * SkeletonDirectoryTable — animated placeholder for the customer directory table.
 */
export function SkeletonDirectoryTable({ rows = 8 }) {
  return (
    <div className="adv-skeleton-table" role="status" aria-busy="true" aria-label="Loading customers">
      {/* Header */}
      <div className="adv-skeleton-table-row" style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
        {[2, 2, 1, 1, 1, 1].map((flex, i) => (
          <div key={i} style={{ flex, minWidth: 0 }}>
            <SkeletonBlock height={10} width="40%" style={{ opacity: 0.5 }} />
          </div>
        ))}
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="adv-skeleton-table-row">
          <div style={{ flex: 2, minWidth: 0 }}>
            <SkeletonBlock height={13} width="65%" />
            <SkeletonBlock height={10} width="45%" style={{ marginTop: 5 }} />
          </div>
          <div style={{ flex: 2, minWidth: 0 }}>
            <SkeletonBlock height={12} width="75%" />
            <SkeletonBlock height={10} width="55%" style={{ marginTop: 5 }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <SkeletonBlock height={22} width={70} style={{ borderRadius: 20 }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <SkeletonBlock height={12} width="70%" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <SkeletonBlock height={12} width="70%" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 6 }}>
              <SkeletonBlock height={32} width={56} style={{ borderRadius: 8 }} />
              <SkeletonBlock height={32} width={56} style={{ borderRadius: 8 }} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/** SkeletonProfilePage — full-page skeleton for customer profile loading. */
export function SkeletonProfilePage() {
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div style={{ height: 56, background: '#0f172a', flexShrink: 0 }} />
      <main className="flex-1 px-8 py-8 max-w-screen-2xl mx-auto w-full">
        <div className="flex items-center gap-3 mb-6">
          {[60, 100, 140].map((w, i) => <span key={i} className="adv-skeleton" style={{ width: w, height: 12, display: 'inline-block' }} />)}
        </div>
        <div className="flex items-center gap-4 mb-8">
          <span className="adv-skeleton" style={{ width: 56, height: 56, borderRadius: '50%', display: 'block', flexShrink: 0 }} />
          <div>
            <span className="adv-skeleton" style={{ width: 200, height: 20, display: 'block', marginBottom: 8 }} />
            <span className="adv-skeleton" style={{ width: 130, height: 14, display: 'block' }} />
          </div>
        </div>
        <div className="flex gap-2 border-b border-slate-200 mb-6">
          {[80, 70].map((w, i) => <span key={i} className="adv-skeleton" style={{ width: w, height: 36, display: 'inline-block', borderRadius: 6 }} />)}
        </div>
        <div className="grid grid-cols-5 gap-6">
          <div className="col-span-2 space-y-4">
            {[5, 3].map((rows, ci) => (
              <div key={ci} className="adv-skeleton-card">
                {Array.from({ length: rows }).map((_, i) => (
                  <span key={i} className="adv-skeleton" style={{ width: ['100%', '70%', '85%', '55%', '90%'][i], height: 13, display: 'block', marginBottom: 10 }} />
                ))}
              </div>
            ))}
          </div>
          <div className="col-span-3 space-y-4">
            {[3, 5].map((rows, ci) => (
              <div key={ci} className="adv-skeleton-card">
                {Array.from({ length: rows }).map((_, i) => (
                  <span key={i} className="adv-skeleton" style={{ width: ['60%', '80%', '100%', '40%', '75%'][i], height: 13, display: 'block', marginBottom: 12 }} />
                ))}
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}

/** SkeletonModal — shimmer rows inside a modal body (advisors list / portal settings). */
export function SkeletonModal({ rows = 4 }) {
  return (
    <div className="space-y-3 py-1" role="status" aria-busy="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 bg-slate-50 rounded-lg px-3 py-3">
          <div className="flex-1">
            <span className="adv-skeleton" style={{ width: '55%', height: 13, display: 'block', marginBottom: 6 }} />
            <span className="adv-skeleton" style={{ width: '75%', height: 11, display: 'block' }} />
          </div>
          <span className="adv-skeleton" style={{ width: 60, height: 28, borderRadius: 6, display: 'block' }} />
        </div>
      ))}
    </div>
  );
}

/** SkeletonHistoryList — shimmer rows for profile history timeline. */
export function SkeletonHistoryList({ rows = 5 }) {
  return (
    <div className="space-y-4 py-2" role="status" aria-busy="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex gap-3">
          <span className="adv-skeleton" style={{ width: 28, height: 28, borderRadius: 8, flexShrink: 0, display: 'block' }} />
          <div className="flex-1">
            <span className="adv-skeleton" style={{ width: '40%', height: 13, display: 'block', marginBottom: 6 }} />
            <span className="adv-skeleton" style={{ width: '70%', height: 11, display: 'block' }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/** SkeletonDashboard — shimmer for the applicant dashboard shell. */
export function SkeletonDashboard() {
  return (
    <div className="mvp-shell" style={{ padding: '24px 22px' }}>
      <div style={{ marginBottom: 24 }}>
        <span className="adv-skeleton" style={{ width: 90, height: 12, display: 'block', marginBottom: 8 }} />
        <span className="adv-skeleton" style={{ width: 180, height: 28, display: 'block' }} />
      </div>
      <div className="adv-skeleton" style={{ height: 140, borderRadius: 22, marginBottom: 20 }} />
      <div style={{ marginBottom: 24 }}>
        <span className="adv-skeleton" style={{ width: 120, height: 14, display: 'block', marginBottom: 14 }} />
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="adv-skeleton" style={{ height: 60, borderRadius: 16, marginBottom: 10 }} />
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="adv-skeleton" style={{ height: 80, borderRadius: 16 }} />
        <div className="adv-skeleton" style={{ height: 80, borderRadius: 16 }} />
      </div>
    </div>
  );
}

/** SkeletonCaseDetail — full-page skeleton for the advisor case detail page. */
export function SkeletonCaseDetail() {
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div style={{ height: 56, background: '#0f172a', flexShrink: 0 }} />
      <main className="flex-1 px-4 py-6 sm:px-8 sm:py-8 max-w-screen-2xl mx-auto w-full">
        {/* Header block */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="space-y-3">
            <span className="adv-skeleton" style={{ width: 240, height: 24, display: 'block' }} />
            <span className="adv-skeleton" style={{ width: 140, height: 14, display: 'block' }} />
          </div>
          <div className="flex gap-2">
            <span className="adv-skeleton" style={{ width: 100, height: 40, borderRadius: 10 }} />
            <span className="adv-skeleton" style={{ width: 140, height: 40, borderRadius: 10 }} />
          </div>
        </div>

        {/* Tabs placeholder */}
        <div className="flex gap-2 border-b border-slate-200 mb-6">
          {[100, 80, 110].map((w, i) => (
            <span key={i} className="adv-skeleton" style={{ width: w, height: 44, marginBottom: -1, borderRadius: '6px 6px 0 0' }} />
          ))}
        </div>

        {/* 2-column layout */}
        <div className="grid gap-6 grid-cols-1 xl:grid-cols-[1fr_38%]">
          {/* Main content area */}
          <div className="space-y-5">
            <div className="adv-skeleton-card">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="space-y-2">
                    <span className="adv-skeleton" style={{ width: 80, height: 10 }} />
                    <span className="adv-skeleton" style={{ width: '80%', height: 14 }} />
                  </div>
                ))}
              </div>
            </div>
            <div className="adv-skeleton-card">
              <span className="adv-skeleton" style={{ width: 120, height: 16, marginBottom: 15 }} />
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="flex justify-between">
                    <span className="adv-skeleton" style={{ width: '40%', height: 12 }} />
                    <span className="adv-skeleton" style={{ width: '30%', height: 12 }} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar area */}
          <div className="space-y-5">
            <div className="adv-skeleton-card">
              <span className="adv-skeleton" style={{ width: 100, height: 14, marginBottom: 15 }} />
              <div className="space-y-4">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="flex gap-3">
                    <span className="adv-skeleton" style={{ width: 24, height: 24, borderRadius: 6 }} />
                    <div className="flex-1 space-y-2">
                      <span className="adv-skeleton" style={{ width: '60%', height: 11 }} />
                      <span className="adv-skeleton" style={{ width: '40%', height: 9 }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

/** SkeletonCaseDetailContent — inner cards and sidebar for case details (no page wrapper). */
export function SkeletonCaseDetailContent() {
  return (
    <div className="grid gap-6 grid-cols-1 xl:grid-cols-[1fr_38%]">
      {/* Main content area */}
      <div className="space-y-5">
        <div className="adv-skeleton-card">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="space-y-2">
                <span className="adv-skeleton" style={{ width: 80, height: 10 }} />
                <span className="adv-skeleton" style={{ width: '80%', height: 14 }} />
              </div>
            ))}
          </div>
        </div>
        <div className="adv-skeleton-card">
          <span className="adv-skeleton" style={{ width: 120, height: 16, marginBottom: 15 }} />
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="flex justify-between">
                <span className="adv-skeleton" style={{ width: '40%', height: 12 }} />
                <span className="adv-skeleton" style={{ width: '30%', height: 12 }} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Sidebar area */}
      <div className="space-y-5">
        <div className="adv-skeleton-card">
          <span className="adv-skeleton" style={{ width: 100, height: 14, marginBottom: 15 }} />
          <div className="space-y-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="flex gap-3">
                <span className="adv-skeleton" style={{ width: 24, height: 24, borderRadius: 6 }} />
                <div className="flex-1 space-y-2">
                  <span className="adv-skeleton" style={{ width: '60%', height: 11 }} />
                  <span className="adv-skeleton" style={{ width: '40%', height: 9 }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
